import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Label, TextInput } from "flowbite-react";
import { useTranslation } from "react-i18next";

const CODE_LENGTH = 6;
/** Matches the gateway's own resend cooldown, so the button re-enables in step with it. */
const RESEND_COOLDOWN_SECONDS = 30;

interface LoginOtpFormProps {
  /** Masked address the code went to, e.g. `ay*******@acme.com`. Null when unknown. */
  destination: string | null;
  /** When the current code stops being accepted. */
  expiresAt: string | null;
  submitting: boolean;
  resending: boolean;
  error: string | null;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onBack: () => void;
}

function formatRemaining(seconds: number): string {
  const clamped = Math.max(0, seconds);
  const mm = Math.floor(clamped / 60);
  const ss = clamped % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

/**
 * Second step of the agent login: the six-digit code the gateway sent through
 * MCont.
 *
 * Kept apart from the credentials form because the two steps share nothing but
 * the card around them — and because the password has to stay in the parent's
 * state across both, which is easier to see when this component never touches
 * it.
 */
export default function LoginOtpForm({
  destination,
  expiresAt,
  submitting,
  resending,
  error,
  onSubmit,
  onResend,
  onBack,
}: LoginOtpFormProps) {
  const { t } = useTranslation("auth");
  const [code, setCode] = useState("");
  const [now, setNow] = useState(() => Date.now());
  const inputRef = useRef<HTMLInputElement>(null);
  const [cooldownUntil, setCooldownUntil] = useState(
    () => Date.now() + RESEND_COOLDOWN_SECONDS * 1000,
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // A new code was issued: clear the old entry and restart both timers.
  useEffect(() => {
    setCode("");
    setCooldownUntil(Date.now() + RESEND_COOLDOWN_SECONDS * 1000);
    inputRef.current?.focus();
  }, [expiresAt]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const secondsLeft = useMemo(() => {
    if (!expiresAt) return null;
    return Math.max(
      0,
      Math.floor((new Date(expiresAt).getTime() - now) / 1000),
    );
  }, [expiresAt, now]);

  const expired = secondsLeft === 0;
  const resendSeconds = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));
  const canSubmit = code.length === CODE_LENGTH && !submitting && !expired;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(code);
  };

  return (
    <form onSubmit={handleSubmit}>
      <h1 className="mb-3 text-2xl font-bold dark:text-white md:text-3xl">
        {t("login.otp.title")}
      </h1>
      <p className="mb-6 text-sm text-gray-500 dark:text-gray-300">
        {destination
          ? t("login.otp.sentTo", { destination })
          : t("login.otp.sentGeneric")}
      </p>

      <div className="mb-4 flex flex-col gap-y-3">
        <Label htmlFor="activation_code">{t("login.otp.codeLabel")}</Label>
        <TextInput
          id="activation_code"
          name="activation_code"
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          placeholder="------"
          className="[&_input]:text-center [&_input]:text-2xl [&_input]:tracking-[0.5em]"
          value={code}
          onChange={(e) =>
            setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))
          }
        />
      </div>

      {secondsLeft !== null && (
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          {expired
            ? t("login.otp.expired")
            : t("login.otp.expiresIn", { time: formatRemaining(secondsLeft) })}
        </p>
      )}

      {error && <p className="mb-4 text-center text-red-500">{error}</p>}

      <div className="mb-4">
        <Button
          type="submit"
          className="bg-gradient-to-br from-purple-600 to-blue-500 text-white hover:bg-gradient-to-bl focus:ring-blue-300 dark:focus:ring-blue-800"
          disabled={!canSubmit}
        >
          {submitting
            ? t("login.otp.isSubmittingText")
            : t("login.otp.submitButton")}
        </Button>
      </div>

      <div className="flex items-center gap-x-4 text-sm">
        <button
          type="button"
          className="text-primary-600 disabled:text-gray-400 dark:text-primary-300 dark:disabled:text-gray-500"
          onClick={onResend}
          disabled={resending || resendSeconds > 0}
        >
          {resendSeconds > 0
            ? t("login.otp.resendIn", { seconds: resendSeconds })
            : t("login.otp.resend")}
        </button>
        <button
          type="button"
          className="text-gray-500 dark:text-gray-300"
          onClick={onBack}
        >
          {t("login.otp.back")}
        </button>
      </div>
    </form>
  );
}
